import { Footer, Header } from '@digital-retail/falabella-ui-cross-components';
import {
  getCommerceHeaders,
  getQuantityMapFromStorage,
  isSodimacRetail,
  isSodimacStandalone,
  isStandalone,
  isTottusStandalone,
  QTY_LIMITS_KEY,
  updateCartQtyLimits,
} from '@digital-retail/store-manager';
import Router, { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { resetDigitalData } from '../../components/Facets/Common/digitalData';
import Loader from '../../components/Loader/Loader';
import apiConfig from '../../config/api/apiConfig';
import { constants } from '../../config/constants';
import { CookieStorage } from '../../utils/CookieStorage';
import { httpService } from '../../utils/httpService';
import { navigateWithUrl, redirect } from '../../utils/RouteHandler';
import { isMarketPlace } from '../../utils/urlHelper';
import { useUserContex } from '../../utils/UserContext';
import { WebStorage } from '../../utils/WebStorage';
import { WithUrl } from '../../utils/WithUrl';
import { layoutStyle } from './Layout.style';

const { DEVICE_DESKTOP } = constants;

const LayoutContainer = ({ children, appCtx }) => {
  const router = useRouter();

  const layoutCss = layoutStyle();
  const [cartCount, setCartCount] = useState('');
  const { refreshUserData, setTick, tick } = useUserContex();
  const isTottusSA = isTottusStandalone(appCtx.store);

  const syncCartQuantities = async (count) => {
    if (count === 0) {
      localStorage.setItem(QTY_LIMITS_KEY, '{}');
      setTick(Date.now());
      return;
    }

    const quantitiesMap = getQuantityMapFromStorage();
    if (quantitiesMap.size !== count) {
      const { regionCode, pid, latLong, deviceType, store } = appCtx;
      const cartId = CookieStorage.getCookie('cartId');
      if (cartId) {
        const mkp = isMarketPlace();
        const headers = getCommerceHeaders({ isMarketPlace: mkp, regionCode, store });
        const response = await httpService(appCtx).get(apiConfig.getCartCount(cartId, regionCode, pid, latLong), {
          headers: {
            ...headers,
            'x-channel-id': deviceType === DEVICE_DESKTOP ? 'WEB' : 'MOBILE',
          },
        });

        if (isTottusSA && response.statusCode === 200) {
          updateCartQtyLimits(response);
        }
      }
    }
    setTick(Date.now());
  };

  useEffect(() => {
    if (appCtx.USER_DATA_COOKIE) {
      const newCartCount = appCtx.USER_DATA_COOKIE.split('*')[1];
      setCartCount(newCartCount);

      if (isTottusSA) {
        syncCartQuantities(parseInt(newCartCount || 0, 10));
      }
    }
  }, [appCtx.USER_DATA_COOKIE]);

  useEffect(() => {
    const handlePageShow = () => {
      if (isTottusSA && tick === 0) {
        const dataCookie = CookieStorage.getCookie('CATALYST_DATA_COOKIE');
        const count = parseInt(dataCookie?.split?.('*')?.[1] || 0, 10);

        syncCartQuantities(count);
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const currentLocation = appCtx.zoneName ?? '';

  const catalystBaseUrl =
    appCtx.header?.entry?.header_configs?.[0]?.links?.catalystbaseurl ?? 'https://www.falabella.com';

  const handleRouteChange = (termo, searchInSis, searchByImage = false) => {
    const SEARCH_PAGE = isStandalone({ store: appCtx.store }) ? 'SA_SEARCH_PAGE' : 'SEARCH_PAGE';
    resetDigitalData();
    if ((appCtx.store && searchInSis) || !appCtx.store) {
      if (!['/search', '/buscar', '/shop', '/b'].includes(router?.route)) {
        if (!searchInSis) {
          const url = WithUrl(SEARCH_PAGE, appCtx.regionCode, {
            custom: {
              searchTerm: termo,
            },
          });
          redirect(url);
        } else {
          const url = WithUrl(
            SEARCH_PAGE,
            appCtx.regionCode,
            {
              custom: {
                searchTerm: termo,
              },
            },
            appCtx.store
          );
          redirect(url);
        }
      } else if (searchByImage) {
        const url = WithUrl(
          SEARCH_PAGE,
          appCtx.regionCode,
          {
            custom: {
              searchTerm: `${termo}&isImageSearch=true`,
            },
          },
          appCtx.store
        );
        WebStorage.setItem({
          name: constants.SESSION_STORAGE_NEW_PAGE,
          shouldPersist: false,
          value: 'true',
        });
        appCtx.setBusy(true);
        redirect(url);
      } else {
        navigateWithUrl({
          site: appCtx.tenant,
          url: `Ntt=${termo}`,
          store: appCtx.store,
          regionCode: appCtx.regionCode,
          onRouteChange: () => {
            WebStorage.setItem({
              name: constants.SESSION_STORAGE_NEW_PAGE,
              shouldPersist: false,
              value: 'true',
            });
            appCtx.setBusy(true);
          },
        });
      }
    } else {
      const url = WithUrl(SEARCH_PAGE, appCtx.regionCode, {
        custom: {
          searchTerm: termo,
        },
      });
      redirect(url);
    }
  };

  const onUserLogin = () => {
    refreshUserData();
  };

  return (
    <>
      <Header
        currentLocation={currentLocation}
        data={{
          header: {
            ...appCtx.header,
            transversalBannerHtml: appCtx.transversalBanner,
          },
          sisNavigationMenu: appCtx.sisNavigationMenu,
          taxonomy: appCtx.taxonomy,
        }}
        isGeoLocationEnabled={appCtx.siteConfig?.toggles?.isGeoLocationEnabled ?? false}
        isTypeAheadEnabled={appCtx.siteConfig?.toggles?.isTypeAheadEnabled ?? false}
        isATGLoginEnabled={appCtx.siteConfig?.toggles?.isATGLoginEnabled ?? false}
        isDefaultComuna={appCtx.isDefaultComuna ?? false}
        selectedZone={appCtx.comuna?.data}
        onUserLogout={appCtx.logoutUser}
        onUserLogin={onUserLogin}
        enableAnalyticsInfoCall
        isZoneModalEnabled
        userCartCount={cartCount}
        parentRouter={Router}
        handleRouteChange={handleRouteChange}
        catalystBaseUrl={catalystBaseUrl}
        store={appCtx.store}
        handleSoftRefresh={appCtx.reloadOnZoneChange}
        isPopOverVisible={false}
        isWebPushNotificationEnabled={appCtx.siteConfig?.toggles?.isWebPushNotificationEnabled ?? false}
        isSISLevel2Header={appCtx.isSISLevel2Header}
        isFingerprintTrackingEnabled={appCtx.siteConfig?.toggles?.isFingerprintTrackingEnabled ?? true}
        isGoogleTagManagerEnabled
        isSodimacStandAloneSite={isSodimacStandalone(appCtx.store) || isSodimacRetail(appCtx.store)}
        isTottusStandAloneSite={isTottusStandalone(appCtx.store)}
      />
      <div className="main">{children}</div>
      {appCtx.busy > 0 && <Loader />}
      <Footer data={appCtx.footer} store={appCtx.store} />
      <style jsx>{layoutCss}</style>
    </>
  );
};

LayoutContainer.propTypes = {
  children: PropTypes.node,
  appCtx: PropTypes.object.isRequired,
};

LayoutContainer.defaultProps = {
  children: null,
};

export { LayoutContainer };
